import React, { useState, useId } from 'react';
import { 
  X, 
  Upload, 
  FileSpreadsheet, 
  CheckCircle2, 
  AlertTriangle, 
  XCircle, 
  Download, 
  Trash2, 
  ArrowRight, 
  RefreshCw, 
  Users,
  Copy,
  Check
} from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { apiWorker } from '../../api/worker';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

export interface ParsedRosterEntry {
  id: string;
  name: string;
  email: string;
  role: string;
  notes?: string;
  status: 'valid' | 'duplicate' | 'invalid_email';
  errorReason?: string;
}

interface RosterCsvImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  nodeId: string;
  roles: string[];
  memberLabel: string;
  existingEmails: Set<string>;
}

export const RosterCsvImportModal: React.FC<RosterCsvImportModalProps> = ({
  isOpen,
  onClose,
  nodeId,
  roles,
  memberLabel,
  existingEmails
}) => {
  const queryClient = useQueryClient();
  const fileInputId = useId();

  const [activeTab, setActiveTab] = useState<'upload' | 'paste'>('upload');
  const [pasteContent, setPasteContent] = useState('');
  const [fileName, setFileName] = useState('');
  const [defaultRole, setDefaultRole] = useState(roles.includes('MEMBER') ? 'MEMBER' : (roles[0] || 'MEMBER'));
  const [parsedEntries, setParsedEntries] = useState<ParsedRosterEntry[]>([]);
  const [step, setStep] = useState<'input' | 'preview' | 'importing' | 'complete'>('input');
  
  // Progress tracking
  const [progressCount, setProgressCount] = useState(0);
  const [successCount, setSuccessCount] = useState(0);
  const [failedEntries, setFailedEntries] = useState<{ email: string; reason: string }[]>([]);

  if (!isOpen) return null;

  // Split line supporting quoted fields
  const parseCsvLine = (text: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    
    // Auto-detect delimiter: comma, tab, or semicolon
    let delimiter = ',';
    if (text.includes('\t') && !text.includes(',')) delimiter = '\t';
    else if (text.includes(';') && !text.includes(',')) delimiter = ';';

    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      if (char === '"') {
        if (inQuotes && text[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === delimiter && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  const processCsvText = (rawText: string) => {
    // Remove BOM and split lines
    const clean = rawText.replace(/^\uFEFF/, '').trim();
    if (!clean) {
      toast.error('The provided CSV file or text is empty.');
      return;
    }

    const lines = clean.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    if (lines.length === 0) {
      toast.error('No lines found to parse.');
      return;
    }

    // Detect header row
    const firstLineCols = parseCsvLine(lines[0]).map(c => c.toLowerCase().replace(/[^a-z]/g, ''));
    let emailColIdx = -1;
    let nameColIdx = -1;
    let firstNameColIdx = -1;
    let lastNameColIdx = -1;
    let roleColIdx = -1;
    let notesColIdx = -1;
    let hasHeader = false;

    firstLineCols.forEach((col, idx) => {
      if (col === 'email' || col === 'emailaddress' || col === 'mail') {
        emailColIdx = idx;
        hasHeader = true;
      } else if (col === 'name' || col === 'fullname' || col === 'member' || col === 'membername' || col === 'athlete' || col === 'swimmer') {
        nameColIdx = idx;
        hasHeader = true;
      } else if (col === 'firstname' || col === 'first') {
        firstNameColIdx = idx;
        hasHeader = true;
      } else if (col === 'lastname' || col === 'last') {
        lastNameColIdx = idx;
        hasHeader = true;
      } else if (col === 'role' || col === 'title' || col === 'type') {
        roleColIdx = idx;
        hasHeader = true;
      } else if (col === 'notes' || col === 'note' || col === 'phone' || col === 'team' || col === 'group') {
        notesColIdx = idx;
        hasHeader = true;
      }
    });

    const dataLines = hasHeader ? lines.slice(1) : lines;
    
    // If no header detected, guess columns by inspection:
    // look for which column has an '@' symbol
    if (!hasHeader && dataLines.length > 0) {
      const sampleCols = parseCsvLine(dataLines[0]);
      sampleCols.forEach((val, idx) => {
        if (val.includes('@') && emailColIdx === -1) {
          emailColIdx = idx;
        } else if (nameColIdx === -1) {
          nameColIdx = idx;
        }
      });
    }

    if (emailColIdx === -1) {
      toast.error('Could not detect an email address column in the CSV.');
      return;
    }

    const seenInBatch = new Set<string>();
    const entries: ParsedRosterEntry[] = [];

    dataLines.forEach((line, idx) => {
      const cols = parseCsvLine(line);
      const rawEmail = (cols[emailColIdx] || '').trim().toLowerCase();
      
      let name = '';
      if (nameColIdx !== -1 && cols[nameColIdx]) {
        name = cols[nameColIdx].trim();
      } else if (firstNameColIdx !== -1 || lastNameColIdx !== -1) {
        const first = firstNameColIdx !== -1 ? cols[firstNameColIdx] || '' : '';
        const last = lastNameColIdx !== -1 ? cols[lastNameColIdx] || '' : '';
        name = `${first} ${last}`.trim();
      }

      let role = defaultRole;
      if (roleColIdx !== -1 && cols[roleColIdx]) {
        const candidate = cols[roleColIdx].trim().toUpperCase();
        if (roles.includes(candidate)) {
          role = candidate;
        }
      }

      const notes = notesColIdx !== -1 && cols[notesColIdx] ? cols[notesColIdx].trim() : undefined;

      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      let status: ParsedRosterEntry['status'] = 'valid';
      let errorReason: string | undefined;

      if (!rawEmail || !emailRegex.test(rawEmail)) {
        status = 'invalid_email';
        errorReason = 'Invalid email format';
      } else if (existingEmails.has(rawEmail)) {
        status = 'duplicate';
        errorReason = 'Already in organization';
      } else if (seenInBatch.has(rawEmail)) {
        status = 'duplicate';
        errorReason = 'Duplicate in this file';
      } else {
        seenInBatch.add(rawEmail);
      }

      entries.push({
        id: `row-${idx}-${Date.now().toString(36)}`,
        name: name || rawEmail.split('@')[0] || 'Member',
        email: rawEmail,
        role,
        notes,
        status,
        errorReason
      });
    });

    setParsedEntries(entries);
    setStep('preview');
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      processCsvText(text);
    };
    reader.onerror = () => {
      toast.error('Failed to read the selected file.');
    };
    reader.readAsText(file);
  };

  const handlePasteSubmit = () => {
    if (!pasteContent.trim()) {
      toast.error('Please paste your CSV rows.');
      return;
    }
    setFileName('Pasted CSV Text');
    processCsvText(pasteContent);
  };

  const handleDownloadSample = () => {
    const csvContent = [
      'Name,Email,Role,Notes',
      'Coach Sarah Miller,sarah@example.com,MEMBER,Head Swim Coach',
      'James Avery,james@example.com,ADMIN,Board President',
      'Alex Rivera,alex@example.com,MEMBER,Parent Volunteer',
      'Jordan Lee,jordan@example.com,MEMBER,Equipment Manager'
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `orgsets_roster_template.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleRemoveEntry = (id: string) => {
    setParsedEntries(prev => prev.filter(e => e.id !== id));
  };

  const handleRoleChange = (id: string, newRole: string) => {
    setParsedEntries(prev => prev.map(e => e.id === id ? { ...e, role: newRole } : e));
  };

  const handleApplyGlobalRole = (newRole: string) => {
    setDefaultRole(newRole);
    setParsedEntries(prev => prev.map(e => ({ ...e, role: newRole })));
  };

  const validEntries = parsedEntries.filter(e => e.status === 'valid');
  const duplicateEntries = parsedEntries.filter(e => e.status === 'duplicate');
  const invalidEntries = parsedEntries.filter(e => e.status === 'invalid_email');

  const executeBatchImport = async () => {
    if (validEntries.length === 0) {
      toast.error('No valid entries to invite.');
      return;
    }

    setStep('importing');
    setProgressCount(0);
    setSuccessCount(0);
    setFailedEntries([]);

    let successful = 0;
    const failed: { email: string; reason: string }[] = [];

    // Process in batches of 3 to avoid throttling while finishing quickly
    const batchSize = 3;
    for (let i = 0; i < validEntries.length; i += batchSize) {
      const batch = validEntries.slice(i, i + batchSize);
      await Promise.all(
        batch.map(async (entry) => {
          try {
            await apiWorker.post(`/api/nodes/${nodeId}/members/invite`, {
              email: entry.email,
              role: entry.role
            });
            successful++;
          } catch (err: any) {
            const reason = err?.response?.data?.message || err?.message || 'Server error';
            failed.push({ email: entry.email, reason });
          } finally {
            setProgressCount(prev => prev + 1);
          }
        })
      );
    }

    setSuccessCount(successful);
    setFailedEntries(failed);
    setStep('complete');

    // Refresh member lists in background
    queryClient.invalidateQueries({ queryKey: ['directory', nodeId] });
    queryClient.invalidateQueries({ queryKey: ['node-pending-invitations', nodeId] });

    if (successful > 0) {
      toast.success(`Successfully dispatched ${successful} ${memberLabel.toLowerCase()} invitations!`);
    }
  };

  const handleReset = () => {
    setStep('input');
    setParsedEntries([]);
    setPasteContent('');
    setFileName('');
    setProgressCount(0);
    setSuccessCount(0);
    setFailedEntries([]);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-150">
      <div className="bg-white dark:bg-[#0A0A0A] border-[4px] border-black dark:border-white w-full max-w-3xl max-h-[90vh] flex flex-col shadow-[12px_12px_0px_#0044FF] dark:shadow-[12px_12px_0px_#FFD600] relative">
        
        {/* Modal Header */}
        <div className="p-6 border-b-4 border-black dark:border-white flex items-center justify-between bg-slate-50 dark:bg-slate-900">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#0044FF] text-white flex items-center justify-center border-2 border-black dark:border-white shadow-[2px_2px_0px_#000]">
              <FileSpreadsheet size={22} />
            </div>
            <div>
              <h2 className="text-xl font-black font-display uppercase tracking-tight text-black dark:text-white">
                Import {memberLabel} Roster
              </h2>
              <p className="text-xs font-bold text-gray-600 dark:text-gray-400">
                Bulk invite contacts from CSV, Excel, Google Sheets, or TeamSnap
              </p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-1.5 border-2 border-black dark:border-white text-gray-500 hover:text-black dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
            aria-label="Close"
          >
            <X size={18} strokeWidth={3} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto flex-1 space-y-6">

          {/* STEP 1: INPUT MODE */}
          {step === 'input' && (
            <div className="space-y-6">
              
              {/* Method Switcher */}
              <div className="flex border-2 border-black dark:border-white">
                <button
                  type="button"
                  onClick={() => setActiveTab('upload')}
                  className={`flex-1 py-3 text-xs font-mono font-black uppercase tracking-wider transition-colors flex items-center justify-center gap-2 ${
                    activeTab === 'upload'
                      ? 'bg-[#0044FF] text-white'
                      : 'bg-white text-black hover:bg-gray-100 dark:bg-black dark:text-white'
                  }`}
                >
                  <Upload size={16} /> Upload CSV File
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('paste')}
                  className={`flex-1 py-3 text-xs font-mono font-black uppercase tracking-wider transition-colors border-l-2 border-black dark:border-white flex items-center justify-center gap-2 ${
                    activeTab === 'paste'
                      ? 'bg-[#0044FF] text-white'
                      : 'bg-white text-black hover:bg-gray-100 dark:bg-black dark:text-white'
                  }`}
                >
                  <Copy size={16} /> Paste Raw CSV Text
                </button>
              </div>

              {/* Upload Dropzone */}
              {activeTab === 'upload' && (
                <div className="border-4 border-dashed border-black dark:border-white p-8 text-center bg-slate-50 dark:bg-slate-900/50 space-y-4">
                  <div className="w-12 h-12 mx-auto bg-white dark:bg-black border-2 border-black dark:border-white flex items-center justify-center shadow-[3px_3px_0px_#000]">
                    <Upload size={24} className="text-[#0044FF]" />
                  </div>
                  <div className="space-y-1">
                    <p className="font-display font-black uppercase text-base text-black dark:text-white">
                      Drop your roster file here or browse
                    </p>
                    <p className="text-xs font-bold text-gray-500">
                      Supports .csv files from Excel, Google Sheets, TeamSnap, SwimTopia, etc.
                    </p>
                  </div>
                  <div>
                    <input 
                      type="file" 
                      id={fileInputId} 
                      accept=".csv,text/csv,text/plain" 
                      onChange={handleFileUpload} 
                      className="hidden" 
                    />
                    <label
                      htmlFor={fileInputId}
                      className="inline-flex items-center gap-2 bg-[#FFD600] hover:bg-[#E6C200] text-black font-black uppercase font-mono tracking-wider text-xs px-5 py-3 border-2 border-black shadow-[3px_3px_0px_#000] cursor-pointer"
                    >
                      Choose CSV File
                    </label>
                  </div>
                </div>
              )}

              {/* Direct Paste */}
              {activeTab === 'paste' && (
                <div className="space-y-3">
                  <label className="block font-mono text-xs font-black uppercase tracking-wider text-black dark:text-white">
                    Paste Rows (Name, Email, Role):
                  </label>
                  <textarea
                    rows={8}
                    value={pasteContent}
                    onChange={(e) => setPasteContent(e.target.value)}
                    placeholder={`Sarah Miller, sarah@example.com, MEMBER\nJames Avery, james@example.com, ADMIN\nAlex Rivera, alex@example.com, MEMBER`}
                    className="w-full font-mono text-xs p-3 border-3 border-black dark:border-white bg-white dark:bg-black text-black dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-0"
                  />
                  <Button
                    onClick={handlePasteSubmit}
                    className="w-full bg-[#0044FF] text-white hover:bg-blue-700 font-black uppercase tracking-wider"
                  >
                    Parse Pasted Text &rarr;
                  </Button>
                </div>
              )}

              {/* Helpful Format Guidance & Template Download */}
              <div className="flex flex-col sm:flex-row items-center justify-between p-4 bg-[#FFD600]/15 border-2 border-black dark:border-white gap-3">
                <div className="text-xs font-bold text-black dark:text-white">
                  <span>Need an example file? Headers like </span>
                  <code className="font-mono bg-white dark:bg-black px-1.5 py-0.5 border border-black text-[11px]">Name, Email, Role</code>
                  <span> are auto-detected.</span>
                </div>
                <button
                  type="button"
                  onClick={handleDownloadSample}
                  className="shrink-0 flex items-center gap-1.5 text-xs font-mono font-black uppercase tracking-wider text-[#0044FF] hover:underline cursor-pointer"
                >
                  <Download size={14} /> Download Sample CSV
                </button>
              </div>

            </div>
          )}

          {/* STEP 2: PREVIEW & COLUMN MAPPING */}
          {step === 'preview' && (
            <div className="space-y-5">
              
              {/* Summary Stats Bar */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-emerald-50 dark:bg-emerald-950/40 border-2 border-emerald-600 text-emerald-900 dark:text-emerald-200">
                  <div className="flex items-center gap-1.5 font-black uppercase text-xs">
                    <CheckCircle2 size={16} className="text-emerald-600" />
                    <span>Ready to Invite</span>
                  </div>
                  <div className="text-2xl font-black font-display mt-1">{validEntries.length}</div>
                </div>

                <div className="p-3 bg-amber-50 dark:bg-amber-950/40 border-2 border-amber-500 text-amber-900 dark:text-amber-200">
                  <div className="flex items-center gap-1.5 font-black uppercase text-xs">
                    <AlertTriangle size={16} className="text-amber-600" />
                    <span>Duplicates</span>
                  </div>
                  <div className="text-2xl font-black font-display mt-1">{duplicateEntries.length}</div>
                </div>

                <div className="p-3 bg-red-50 dark:bg-red-950/40 border-2 border-red-500 text-red-900 dark:text-red-200">
                  <div className="flex items-center gap-1.5 font-black uppercase text-xs">
                    <XCircle size={16} className="text-red-600" />
                    <span>Invalid Email</span>
                  </div>
                  <div className="text-2xl font-black font-display mt-1">{invalidEntries.length}</div>
                </div>
              </div>

              {/* Default Role Modifier */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-3.5 bg-slate-100 dark:bg-slate-900 border-2 border-black dark:border-white gap-3">
                <div className="text-xs font-bold text-black dark:text-white">
                  <span>File: <strong>{fileName}</strong> ({parsedEntries.length} total rows)</span>
                </div>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <span className="text-xs font-mono uppercase font-bold text-gray-500 whitespace-nowrap">Default Role:</span>
                  <select 
                    value={defaultRole}
                    onChange={(e) => handleApplyGlobalRole(e.target.value)}
                    className="border-2 border-black dark:border-white bg-white dark:bg-black font-mono text-xs px-2 py-1.5 font-bold outline-none"
                  >
                    {roles.map(r => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Parsed Rows Table */}
              <div className="border-2 border-black dark:border-white overflow-x-auto max-h-64">
                <table className="w-full text-left text-xs font-mono">
                  <thead className="bg-black text-white dark:bg-white dark:text-black sticky top-0">
                    <tr>
                      <th className="p-2.5 font-black uppercase">Name</th>
                      <th className="p-2.5 font-black uppercase">Email Address</th>
                      <th className="p-2.5 font-black uppercase">Role</th>
                      <th className="p-2.5 font-black uppercase">Status</th>
                      <th className="p-2.5 text-right font-black uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-slate-800">
                    {parsedEntries.map((entry) => (
                      <tr 
                        key={entry.id} 
                        className={
                          entry.status === 'valid'
                            ? 'bg-white dark:bg-black text-black dark:text-white'
                            : 'bg-gray-50 dark:bg-slate-900/60 opacity-60'
                        }
                      >
                        <td className="p-2.5 font-bold truncate max-w-[140px]">{entry.name}</td>
                        <td className="p-2.5 truncate max-w-[200px]">{entry.email || <span className="italic text-gray-400">Missing</span>}</td>
                        <td className="p-2.5">
                          {entry.status === 'valid' ? (
                            <select
                              value={entry.role}
                              onChange={(e) => handleRoleChange(entry.id, e.target.value)}
                              className="border border-black dark:border-white bg-transparent text-[11px] font-bold px-1 py-0.5"
                            >
                              {roles.map(r => (
                                <option key={r} value={r}>{r}</option>
                              ))}
                            </select>
                          ) : (
                            <span className="text-[11px] text-gray-500">{entry.role}</span>
                          )}
                        </td>
                        <td className="p-2.5">
                          {entry.status === 'valid' && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40 px-1.5 py-0.5 border border-emerald-500">
                              <Check size={10} /> Valid
                            </span>
                          )}
                          {entry.status === 'duplicate' && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase text-amber-600 bg-amber-50 dark:bg-amber-950/40 px-1.5 py-0.5 border border-amber-500">
                              {entry.errorReason}
                            </span>
                          )}
                          {entry.status === 'invalid_email' && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase text-red-600 bg-red-50 dark:bg-red-950/40 px-1.5 py-0.5 border border-red-500">
                              {entry.errorReason}
                            </span>
                          )}
                        </td>
                        <td className="p-2.5 text-right">
                          <button
                            type="button"
                            onClick={() => handleRemoveEntry(entry.id)}
                            className="p-1 text-gray-400 hover:text-red-600 cursor-pointer transition-colors"
                            aria-label="Remove row"
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Bottom Actions */}
              <div className="pt-2 flex flex-col sm:flex-row items-center justify-between gap-3 border-t-2 border-black dark:border-white">
                <Button variant="ghost" onClick={handleReset} className="w-full sm:w-auto">
                  &larr; Choose Different File
                </Button>

                <Button
                  onClick={executeBatchImport}
                  disabled={validEntries.length === 0}
                  className="w-full sm:w-auto bg-[#0044FF] hover:bg-blue-700 text-white font-black uppercase tracking-wider px-6 py-3"
                >
                  Invite {validEntries.length} {validEntries.length === 1 ? memberLabel : `${memberLabel}s`} &rarr;
                </Button>
              </div>

            </div>
          )}

          {/* STEP 3: IMPORTING PROGRESS */}
          {step === 'importing' && (
            <div className="py-12 px-4 text-center space-y-6">
              <div className="w-16 h-16 mx-auto bg-[#0044FF] text-white flex items-center justify-center border-4 border-black dark:border-white shadow-[4px_4px_0px_#000] animate-spin">
                <RefreshCw size={28} />
              </div>

              <div className="space-y-2">
                <h3 className="text-xl font-black font-display uppercase tracking-tight text-black dark:text-white">
                  Dispatching Invitations...
                </h3>
                <p className="text-sm font-bold text-gray-600 dark:text-gray-400">
                  Sending {progressCount} of {validEntries.length} ({Math.round((progressCount / validEntries.length) * 100)}%)
                </p>
              </div>

              <div className="w-full bg-gray-200 dark:bg-slate-800 border-2 border-black dark:border-white h-5 p-0.5">
                <div 
                  className="bg-[#0044FF] h-full transition-all duration-200"
                  style={{ width: `${Math.round((progressCount / validEntries.length) * 100)}%` }}
                />
              </div>

              <p className="text-xs text-gray-500 font-mono">
                Please do not close this window while invites are processing.
              </p>
            </div>
          )}

          {/* STEP 4: COMPLETE */}
          {step === 'complete' && (
            <div className="space-y-6 py-4">
              <div className="p-6 bg-emerald-50 dark:bg-emerald-950/30 border-4 border-emerald-600 text-center space-y-2">
                <div className="w-12 h-12 mx-auto bg-emerald-600 text-white flex items-center justify-center border-2 border-black shadow-[2px_2px_0px_#000]">
                  <CheckCircle2 size={24} />
                </div>
                <h3 className="text-2xl font-black font-display uppercase tracking-tight text-emerald-950 dark:text-emerald-100">
                  Roster Import Finished!
                </h3>
                <p className="text-sm font-bold text-emerald-800 dark:text-emerald-300">
                  Successfully invited <strong>{successCount}</strong> {memberLabel.toLowerCase()}s to your organization.
                </p>
              </div>

              {failedEntries.length > 0 && (
                <div className="border-2 border-red-500 p-4 space-y-2 bg-red-50 dark:bg-red-950/30">
                  <div className="text-xs font-black uppercase text-red-700 flex items-center gap-1.5">
                    <AlertTriangle size={14} />
                    <span>{failedEntries.length} Invitations Encountered Errors:</span>
                  </div>
                  <ul className="text-xs font-mono space-y-1 text-red-800 dark:text-red-300 max-h-32 overflow-y-auto">
                    {failedEntries.map((f, i) => (
                      <li key={i}>• {f.email}: {f.reason}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="p-4 bg-slate-50 dark:bg-slate-900 border-2 border-black dark:border-white text-xs font-bold space-y-1">
                <div className="text-black dark:text-white">Next Steps:</div>
                <div className="text-gray-600 dark:text-gray-400">
                  Invited members can click their email link or go to your hub to accept. Their statuses will appear as <strong>PENDING</strong> in your directory until accepted.
                </div>
              </div>

              <div className="pt-2 flex justify-end">
                <Button 
                  onClick={onClose}
                  className="bg-[#0044FF] text-white hover:bg-blue-700 font-black uppercase tracking-wider px-8 py-3"
                >
                  Done
                </Button>
              </div>
            </div>
          )}

        </div>

      </div>
    </div>
  );
};

